import uuid from 'uuid/v4';
import Cookies from 'js-cookie';
import { ActionTypes } from '../state/ActionTypes';
import { notificationsSlice } from '../state/slices/notificationsSlice';

import {
  Notification,
  NotificationScopes,
  NotificationTypes,
} from '../values/Notification';

const CLOSED_NOTIFICATIONS_COOKIE_KEY = 'mc-closed-notifications';

const getClosedNotifications = () =>
  Cookies.getJSON(CLOSED_NOTIFICATIONS_COOKIE_KEY) || [];
const setClosedNotifications = (notifs) =>
  Cookies.set(CLOSED_NOTIFICATIONS_COOKIE_KEY, notifs, { expires: 365 * 4 });

const getRemovedNotificationAndIndex = (id, _state) => {
  const removedNotificationIndex = _state.visibleNotifications.findIndex(
    (notif) => notif.id === id
  );
  const removedNotification =
    _state.visibleNotifications[removedNotificationIndex];
  return { removedNotificationIndex, removedNotification };
};

const isNotificationAlreadyInCookie = (removedNotification) => {
  const closedNotifications = getClosedNotifications();
  const index = closedNotifications.indexOf(removedNotification.scope);
  // check if notification is already added to cookies
  return index !== -1;
};

const removeNotification = (
  id,
  {
    comesFromDirectUserAction = true,
    isHardCloseCheckboxChecked = false,
    shouldCloseVisibleNotification = true,
  } = {},
  _state
) => {
  const { removedNotification, removedNotificationIndex } =
    getRemovedNotificationAndIndex(id, _state);
  /**
   * Sometimes `removeNotification()` called right after we've reset the app state
   * (this happens in `AppActionCreators.saveDrafts`) and so `state.visibleNotifications`
   * is empty. This check ensures we don't try and remove a non-existant notification.
   */
  if (!removedNotification) {
    return;
  }

  if (shouldCloseVisibleNotification) {
    _state.visibleNotifications.splice(removedNotificationIndex, 1);
  }

  const rememberClosedNotification =
    removedNotification.onlyCloseOnce &&
    (!removedNotification.showSoftAndHardCloseOptions ||
      (removedNotification.showSoftAndHardCloseOptions &&
        isHardCloseCheckboxChecked)) &&
    comesFromDirectUserAction &&
    !isNotificationAlreadyInCookie(removedNotification);

  if (rememberClosedNotification) {
    const closedNotifications = getClosedNotifications();
    setClosedNotifications([...closedNotifications, removedNotification.scope]);
  }
};

const removeAllNotificationsByScope = (
  scope,
  comesFromDirectUserAction,
  _state
) => {
  const notifications = _state.visibleNotifications.filter(
    (notif) => notif.scope === scope
  );
  notifications.forEach((notif) =>
    removeNotification(notif.id, { comesFromDirectUserAction }, _state)
  );
};

const removeNotificationCookie = (id, _state) => {
  const { removedNotification } = getRemovedNotificationAndIndex(id, _state);
  if (!removedNotification) {
    return;
  }
  const closedNotifications = getClosedNotifications();
  const index = closedNotifications.indexOf(removedNotification.scope);
  if (isNotificationAlreadyInCookie(removedNotification)) {
    closedNotifications.splice(index, 1);
    setClosedNotifications(closedNotifications);
  }
};
const removeComposerOmniboxNotices = (draftId, _state) => {
  const notifications = _state.visibleNotifications.filter(
    (notif) =>
      notif.data.id === draftId &&
      notif.scope === NotificationScopes.MC_OMNIBOX_EDIT_NOTICE
  );
  notifications.forEach((notif) => removeNotification(notif.id, {}, _state));
};
const shouldShowNotification = (notification) => {
  if (!notification.onlyCloseOnce) return true;

  const closedNotifications = getClosedNotifications();
  const wasAlreadyClosed = closedNotifications.includes(notification.scope);

  return !wasAlreadyClosed;
};

export const notificationReducer = (draft, action) => {
  switch (action.actionType) {
    case ActionTypes.QUEUE_NOTIFICATION:
      {
        const { isUnique, ...actionData } = action;
        // Remove prev notifications that share the same scope when a scope is identified as unique
        // eslint-disable-next-line no-use-before-define
        if (isUnique)
          removeAllNotificationsByScope(actionData.scope, false, draft);

        const id = uuid();
        let notification;

        switch (actionData.type) {
          case NotificationTypes.ERROR:
            notification = Notification.getNewErrorNotification(id, actionData);
            break;

          case NotificationTypes.SUCCESS:
            notification = Notification.getNewSuccessNotification(
              id,
              actionData
            );
            break;

          case NotificationTypes.INFO:
            notification = Notification.getNewInfoNotification(id, actionData);
            break;

          default:
            return;
        }

        if (shouldShowNotification(notification))
          draft.visibleNotifications.push(notification);
      }
      break;

    case ActionTypes.REMOVE_NOTIFICATION:
      removeNotification(action.id, action.data, draft);
      break;

    case ActionTypes.REMOVE_NOTIFICATION_COOKIE:
      removeNotificationCookie(action.id, draft);
      break;

    case ActionTypes.REMOVE_ALL_NOTIFICATIONS_BY_SCOPE:
      removeAllNotificationsByScope(
        action.scope,
        action.comesFromDirectUserAction,
        draft
      );
      break;

    case ActionTypes.REMOVE_COMPOSER_NOTICES:
      removeComposerOmniboxNotices(action.draftId, draft);
      break;

    case ActionTypes.APP_RESET:
    case ActionTypes.APP_SOFT_RESET:
      // we need to return the new state here because
      // doing `draft = notificationsSlice.getInitialState()` would replace the
      // object reference and won't work
      return notificationsSlice.getInitialState();
    default:
  }
};
